/**
 * Board geometry: the king-move neighbourhood and the three families of units.
 *
 * Every rule in Star Battle is either "this unit holds exactly two stars" or "no
 * two stars touch". Rows, columns, and regions are interchangeable as far as the
 * first rule goes, so the solver treats them as one flat list of units and never
 * special-cases which family a unit came from — except in the set-confinement
 * technique, which is explicitly about one family constraining another.
 *
 * The board never changes size, so everything here that depends only on the grid
 * is computed once at module load. Only the region map varies per puzzle.
 */

import { CELLS, SIZE } from './types';

/**
 * Rows, columns, and regions — the three unit families every cell belongs to,
 * in the order `unitsOfCell` stores them and `units` is laid out.
 */
export const FAMILY_COUNT = 3;

function buildNeighbors(diagonals: boolean): number[][] {
  const all: number[][] = [];
  for (let index = 0; index < CELLS; index += 1) {
    const row = Math.floor(index / SIZE);
    const col = index % SIZE;
    const list: number[] = [];
    for (let dr = -1; dr <= 1; dr += 1) {
      for (let dc = -1; dc <= 1; dc += 1) {
        if (dr === 0 && dc === 0) continue;
        if (!diagonals && dr !== 0 && dc !== 0) continue;
        const r = row + dr;
        const c = col + dc;
        if (r < 0 || c < 0 || r >= SIZE || c >= SIZE) continue;
        list.push(r * SIZE + c);
      }
    }
    all.push(list);
  }
  return all;
}

/** The eight touching cells — the adjacency rule's neighbourhood. */
export const NEIGHBORS: readonly number[][] = buildNeighbors(true);
/** The four edge-sharing cells, for region connectivity and growth. */
export const ORTHO: readonly number[][] = buildNeighbors(false);

/**
 * Every way to place two non-touching stars in one row.
 *
 * The generator walks these instead of individual cells when it needs a random
 * placement: 36 options at 10×10, versus picking two cells and rejecting.
 */
export const ROW_PLACEMENTS: readonly (readonly number[])[] = (() => {
  const out: number[][] = [];
  for (let first = 0; first < SIZE; first += 1) {
    for (let second = first + 2; second < SIZE; second += 1) {
      out.push([first, second]);
    }
  }
  return out;
})();

export interface Geometry {
  /** 3·SIZE units: rows first, then columns, then regions. */
  units: number[][];
  /** The row, column, and region unit each cell belongs to. */
  unitsOfCell: Int32Array;
  /** `contains[u][cell]` — membership test without scanning the unit. */
  contains: Uint8Array[];
}

export function buildGeometry(regions: readonly number[]): Geometry {
  const unitCount = SIZE * FAMILY_COUNT;
  const units: number[][] = Array.from({ length: unitCount }, () => []);
  const unitsOfCell = new Int32Array(CELLS * FAMILY_COUNT);

  for (let index = 0; index < CELLS; index += 1) {
    const rowUnit = Math.floor(index / SIZE);
    const colUnit = SIZE + (index % SIZE);
    const regionUnit = SIZE * 2 + (regions[index] as number);
    (units[rowUnit] as number[]).push(index);
    (units[colUnit] as number[]).push(index);
    (units[regionUnit] as number[]).push(index);
    unitsOfCell[index * FAMILY_COUNT] = rowUnit;
    unitsOfCell[index * FAMILY_COUNT + 1] = colUnit;
    unitsOfCell[index * FAMILY_COUNT + 2] = regionUnit;
  }

  const contains = units.map((cells) => {
    const mask = new Uint8Array(CELLS);
    for (const cell of cells) mask[cell] = 1;
    return mask;
  });

  return { units, unitsOfCell, contains };
}

// ── How the regions look ────────────────────────────────────────────────────

/**
 * The smallest perimeter `area` cells can be arranged into.
 *
 * The bound for polyominoes, `2·⌈2√a⌉`, and it is met by the square-ish ones:
 * four cells in a 2×2 block have perimeter 8, and so does a 1×3 line plus its
 * two ends. Everything raggeder is longer.
 */
export function minPerimeter(area: number): number {
  return 2 * Math.ceil(2 * Math.sqrt(area));
}

/**
 * How blobby a region map is, from 0 to 1.
 *
 * Each region is scored against the tightest shape of its size — 1 is a block,
 * a long snake is around 0.45 — and the board is the mean of those. A cheap
 * stand-in for "does this look drawn or does it look like tetris gone wrong",
 * which is a real part of how hard a board feels: a player tracing a region that
 * wanders across six rows is doing bookkeeping, not deduction.
 *
 * The grid edge counts as perimeter, so a region hugging the wall gets no
 * discount for the side it cannot be measured on. That keeps the mean honest —
 * every region is compared against the same bound.
 */
export function smoothness(regions: readonly number[]): number {
  const area = new Array<number>(SIZE).fill(0);
  const perimeter = new Array<number>(SIZE).fill(0);
  for (let cell = 0; cell < CELLS; cell += 1) {
    const id = regions[cell] as number;
    let same = 0;
    for (const neighbor of ORTHO[cell] as number[]) {
      if ((regions[neighbor] as number) === id) same += 1;
    }
    area[id] = (area[id] as number) + 1;
    perimeter[id] = (perimeter[id] as number) + 4 - same;
  }

  let total = 0;
  let counted = 0;
  for (let id = 0; id < SIZE; id += 1) {
    const cells = area[id] as number;
    if (cells === 0) continue;
    total += minPerimeter(cells) / (perimeter[id] as number);
    counted += 1;
  }
  return counted === 0 ? 0 : total / counted;
}

/**
 * The change in total perimeter from moving one cell between regions.
 *
 * Falls out of counting edges: the cell's old region gains a face for every
 * neighbour it kept, the new one loses a face for every neighbour it already
 * had. So a move is smoothing exactly when the cell has more neighbours where it
 * is going than where it is — negative is tidier, and no area needs looking up.
 */
export function perimeterDelta(regions: readonly number[], cell: number, to: number): number {
  const from = regions[cell] as number;
  let home = 0;
  let away = 0;
  for (const neighbor of ORTHO[cell] as number[]) {
    const owner = regions[neighbor] as number;
    if (owner === from) home += 1;
    else if (owner === to) away += 1;
  }
  return 2 * (home - away);
}

// ── Symmetry ────────────────────────────────────────────────────────────────

/**
 * The cell a half turn of the board puts this one on.
 *
 * Row-major order makes it one subtraction: turning the grid end over end
 * reverses the order its cells are read in. The board is ten wide, so no cell
 * lands on itself.
 */
export function turned(cell: number): number {
  return CELLS - 1 - cell;
}

/**
 * How nearly a region map is a half turn of itself, from 0 to 1.
 *
 * Each region is matched with the region most of its turned cells land in, and
 * this is the share of cells that land where their region's match says they
 * should. A symmetric map is 1 whatever its regions are numbered; a map with a
 * cell or two out of place is a little under.
 */
export function symmetryOf(regions: readonly number[]): number {
  const votes = Array.from({ length: SIZE }, () => new Array<number>(SIZE).fill(0));
  for (let cell = 0; cell < CELLS; cell += 1) {
    const row = votes[regions[cell] as number] as number[];
    const twin = regions[turned(cell)] as number;
    row[twin] = (row[twin] as number) + 1;
  }
  let matched = 0;
  for (const row of votes) matched += Math.max(...row);
  return matched / CELLS;
}

/** Whether a set of cells is orthogonally connected. */
export function isConnected(cells: readonly number[]): boolean {
  if (cells.length === 0) return false;
  const member = new Set(cells);
  const seen = new Set<number>([cells[0] as number]);
  const stack = [cells[0] as number];
  while (stack.length > 0) {
    const cell = stack.pop() as number;
    for (const next of ORTHO[cell] as number[]) {
      if (!member.has(next) || seen.has(next)) continue;
      seen.add(next);
      stack.push(next);
    }
  }
  return seen.size === cells.length;
}
